import React, {useState, useEffect, useMemo} from "react";
import { addDaysToDate, setUserSession, getUser, getToken, mobileCheck, pwaInstalled, notificationsAreSupported } from "../utils/Common";
import { patchUser, getUserMotions, getSharedCaps, getUserMe } from "../utils/bedrockAPI";
import { Button } from "react-bootstrap";
import "./Nudge.css";

// Nudge timing and frequency
const NUDGE_MIN_ACCOUNT_AGE_DAYS = 7;
const NUDGE_MASTER_COOLDOWN_DAYS = 1;
const NUDGE_TOPIC_COOLDOWN_DAYS = 3;
const NUDGE_DISMISS_RETRY_DAYS = 90;

// Nudge fibonacci counters (tracks how many times a topic has been shown)
const NUDGE_DEFAULT_FIBONACCI = 1;
const NUDGE_TOO_MANY_TIMES_DEFAULT = 5;
const NUDGE_TOO_MANY_TIMES_PWA = 10;
const NUDGE_PERMABLOCK_FIBONACCI = 100;

// Nudge type keys
const NUDGE_TYPE_NOTIFICATIONS = "notifications";
const NUDGE_TYPE_PWA = "pwa";
const NUDGE_TYPE_BLOCKED = "blocked";
const NUDGE_TYPE_PERMABLOCKED = "permablocked";
const NUDGE_TYPE_APP_NOTIFICATIONS = "appNotifications";
const NUDGE_TYPE_DESKTOP_NOTIFICATIONS = "desktopNotifications";
const NUDGE_TYPE_BLOCKED_APP_NOTIFICATIONS = "blockedAppNotifications";
const NUDGE_TYPE_BLOCKED_DESKTOP_NOTIFICATIONS = "blockedDesktopNotifications";
const NUDGE_TYPE_MOTION_APP_NOTIFICATIONS = "motionAppNotifications";
const NUDGE_TYPE_MOTION_DESKTOP_NOTIFICATIONS = "motionDesktopNotifications";
const NUDGE_TYPE_POWER_APP_NOTIFICATIONS = "powerAppNotifications";
const NUDGE_TYPE_POWER_DESKTOP_NOTIFICATIONS = "powerDesktopNotifications";

const DISMISS_RETRY_NUDGE_TYPES = [
    NUDGE_TYPE_BLOCKED_APP_NOTIFICATIONS,
    NUDGE_TYPE_BLOCKED_DESKTOP_NOTIFICATIONS,
    NUDGE_TYPE_APP_NOTIFICATIONS,
    NUDGE_TYPE_DESKTOP_NOTIFICATIONS,
];

export default function Nudge({setErrorStatus,setErrorMessage,setShowError,showError,history}) {

    const [userMotions, setUserMotions] = useState(null);
    const [sharedCaps, setSharedCaps] = useState(null);
    const [userMeetsRequirements, setUserMeetsRequirements] = useState(false);
    const [user, setUser] = useState(null);

    async function navigateAndDismiss(nudgeType) {
        setShowError(false);
        setErrorMessage("");
        if(nudgeType===NUDGE_TYPE_NOTIFICATIONS) {
            history.push('/account/notifications/nudge');
        } else if (nudgeType===NUDGE_TYPE_PWA) {
            history.push('/help/app');
            // window.open("https://data.bedrockwireless.com/help/app","_blank");
        } else if (nudgeType===NUDGE_TYPE_BLOCKED && mobileCheck()) {
            history.push('/help/mobile');
            // window.open("https://data.bedrockwireless.com/help/mobile","_blank");
        } else if (nudgeType===NUDGE_TYPE_BLOCKED && !mobileCheck()) {
            history.push('/help/desktop');
            // window.open("https://data.bedrockwireless.com/help/desktop","_blank");
        } else if (nudgeType.startsWith("motion")) {
            history.push('/account/notifications/motion');
        } else if (nudgeType.startsWith("power")) {
            history.push('/account/notifications/power');
        }
    }

    async function handleDismiss(nudgeType,dismiss=false) {
        if(nudgeType===NUDGE_TYPE_BLOCKED) {
            updateNudge(user,NUDGE_TYPE_PERMABLOCKED);
        } else if(dismiss) {
            updateNudge(user, nudgeType, true);
        }
        setShowError(false);
        setErrorMessage("");
    }

    const NudgeBlock = ({text,affirmitive,nudgeType,dismiss}) => {
        return (
            <div className="nudge-block">
                <div className="nudge-block-text">{text}</div>
                <div className="nudge-block-actions">
                    {nudgeType!==NUDGE_TYPE_BLOCKED && (
                        <Button
                            className="nudge-btn-secondary"
                            onClick={() => handleDismiss(nudgeType)}
                        >
                            Remind Me Later
                        </Button>
                    )}
                    <Button
                        className="nudge-btn-primary"
                        onClick={() => navigateAndDismiss(nudgeType)}
                    >
                        {affirmitive}
                    </Button>
                    {dismiss && (
                        <Button
                            className="nudge-btn-secondary"
                            onClick={() => handleDismiss(nudgeType, true)}
                        >
                            Dismiss
                        </Button>
                    )}
                </div>
            </div>
        );
    };

    const notificationsContent = useMemo(() => 
        <NudgeBlock 
            text={"We've added"+(mobileCheck() ? " mobile app": " desktop") +" notifications. Click below to check it out!"}
            affirmitive="Go to Notification Settings"
            nudgeType={NUDGE_TYPE_NOTIFICATIONS}
        />
    ,[]);

    const notificationsDismissContent = useMemo(() => 
        <NudgeBlock
            text="Friendly reminder: Notifications can greatly improve your user experience. Click below to turn these on."
            affirmitive={mobileCheck() ? "Notification Settings" : "Go to Notification Settings"}
            nudgeType={NUDGE_TYPE_NOTIFICATIONS}
            dismiss={true}
        />
    ,[]);

    const pwaContent = useMemo(() => 
        <NudgeBlock
            text="You can now install our web app on your mobile device! Click below to see instructions."
            affirmitive="View Instructions"
            nudgeType={NUDGE_TYPE_PWA}
        />
    ,[]);

    const pwaDismissContent = useMemo(() => 
        <NudgeBlock
            text="Friendly reminder: downloading our web app will result in a better user experience. Click below for instructions, or access these instructions under 'Help'"
            affirmitive="View Instructions"
            nudgeType={NUDGE_TYPE_PWA}
            dismiss={true}
        />
    ,[]);

    const blockedContent = useMemo(() =>
        <NudgeBlock
            text={"It looks like you’ve requested notifications for this type of device but you’re currently blocking notifications in your "+(mobileCheck()?"app":"browser")+".  Would you like instructions for allowing notifications?"}
            affirmitive="View Instructions"
            nudgeType={NUDGE_TYPE_BLOCKED}
            dismiss={true}
        />
    ,[]);

    const motionContent = useMemo(() => 
        <NudgeBlock
            text={"Would you like to configure your motion notifications? Click below to access those settings for "+(mobileCheck()?"mobile":"desktop")}
            affirmitive={mobileCheck() ? "Motion Notifications" : "Go to Motion Notification Settings"}
            nudgeType={mobileCheck() ? NUDGE_TYPE_MOTION_APP_NOTIFICATIONS : NUDGE_TYPE_MOTION_DESKTOP_NOTIFICATIONS}
            dismiss={true}
        />
    ,[]);

    const powerContent = useMemo(() => 
        <NudgeBlock
            text={"Would you like to get power outage notifications? Click below to access those settings for "+(mobileCheck()?"mobile":"desktop")}
            affirmitive={mobileCheck() ? "Power Notifications" : "Go to Power Notification Settings"}
            nudgeType={mobileCheck() ? NUDGE_TYPE_POWER_APP_NOTIFICATIONS : NUDGE_TYPE_POWER_DESKTOP_NOTIFICATIONS}
            dismiss={true}
        />
    ,[]);

    const updateNudge = async function (u,nudgeType,dismiss=false) { // Should this be an api call?
        const newNudgeDate = (new Date()).toISOString();
        let newSettings;
        if(!u) {
            u = getUser();
            setUser(u);
        }
        if(!u.settings) {
            newSettings = {};
        } else {
            newSettings = JSON.parse(JSON.stringify(u.settings));
        }
        // default fib is 1
        let fibonacci = NUDGE_DEFAULT_FIBONACCI;
        if(!newSettings.nudge) {
            newSettings.nudge = {};
        } else {
            // Get fib for nudge type
            if(newSettings.nudge[nudgeType]) {
                if(newSettings.nudge[nudgeType].fibonacci) {
                    fibonacci = newSettings.nudge[nudgeType].fibonacci+1;
                }
            }
        }
        // Master nudge so we don't nudge multiple things in a row
        newSettings.nudge.lastNudge = {date:newNudgeDate};
        // When the user dismisses a blocked notification warning (they really want it blocked)
        if(nudgeType===NUDGE_TYPE_PERMABLOCKED) {
            if(mobileCheck()) {
                newSettings.nudge[NUDGE_TYPE_BLOCKED_APP_NOTIFICATIONS] = {date:newNudgeDate,fibonacci:NUDGE_PERMABLOCK_FIBONACCI,dismiss:true}
            } else {
                newSettings.nudge[NUDGE_TYPE_BLOCKED_DESKTOP_NOTIFICATIONS] = {date:newNudgeDate,fibonacci:NUDGE_PERMABLOCK_FIBONACCI,dismiss:true}
            }
        } else {
            newSettings.nudge[nudgeType] = {date:newNudgeDate,fibonacci:fibonacci}
            if(dismiss) {
                newSettings.nudge[nudgeType].dismiss = true;
                if(u.settings && u.settings.nudge && u.settings.nudge[nudgeType] && u.settings.nudge[nudgeType].date) {
                    if(DISMISS_RETRY_NUDGE_TYPES.includes(nudgeType)) {
                        if(addDaysToDate(u.settings.nudge[nudgeType].date,NUDGE_DISMISS_RETRY_DAYS)>newNudgeDate) {
                            // Reset the dismiss value every 90 days on blocked nudges
                            newSettings.nudge[nudgeType].dismiss = false; 
                        }
                    }
                }
            }
        }
        await patchUser(u.userId,{settings:newSettings});
        let tempUser = JSON.parse(JSON.stringify(u));
        tempUser.settings = newSettings;
        setUser(tempUser);
    }

    const shouldINudge = function (u,nudgeType) { // Should this be an API call?
        const now = new Date();
        if(!u) u = getUser();
        // User must be at least a week old
        if(u && u.addDate) {
            if(addDaysToDate(u.addDate,NUDGE_MIN_ACCOUNT_AGE_DAYS)>now) {
                return false;
            }
        }
        if(u && u.settings && u.settings.nudge) {
            // console.log(u.settings.nudge);
            let nudgeOkay = false;
            if(u.settings.nudge.lastNudge) {
                // console.log(u.settings.nudge.lastNudge);
                const masterNudge = u.settings.nudge.lastNudge.date;
                if(addDaysToDate(masterNudge,NUDGE_MASTER_COOLDOWN_DAYS)<now) {
                // if(addHoursToDate(masterNudge,0.001)>now) { // TESTING TODO SWAP
                    // user was nudged (any category) over a day ago
                    nudgeOkay = true;
                }
            } else {
                // user has never been nudged
                nudgeOkay = true;
            }
            if(nudgeOkay) { // master nudge is older than 1 day (nudge once per day max)
                if(u.settings.nudge[nudgeType]) {
                    const lastNudge = u.settings.nudge[nudgeType].date;
                    const dismissed = u.settings.nudge[nudgeType].dismiss;
                    if(dismissed) { 
                        // If the user keeps blocking notifications, reattmpt every 90 days
                        if(DISMISS_RETRY_NUDGE_TYPES.includes(nudgeType)) {
                            if(addDaysToDate(lastNudge,NUDGE_DISMISS_RETRY_DAYS)>now) {
                                return true;
                            }
                        }
                        // We've already shown the nudge 5 times over 15 days or they dismissed the nudge
                        return false;
                    }
                    if(addDaysToDate(lastNudge,NUDGE_TOPIC_COOLDOWN_DAYS)<now) { // Show nudge every 3 days
                    // if(addHoursToDate(lastNudge,0.01)<now) { // TESTING TODO SWAP
                        return true;
                    }
                    // user was just nudged on this topic
                    return false;
                } else {
                    // user hasn't been nudged on this topic yet
                    return true;
                }
            } else {
                // user was nudged by any nudegType less than 1 day ago
                return false;
            }
        } else {
            // user hasn't had any nudge interaction yet
            return true;
        }
    }

    async function showNudge(user,content,update) {
        // console.log("who is calling me",update);
        setErrorMessage(content);
        setErrorStatus("nudge-message");
        setShowError(true);
        updateNudge(user,update);
    }

    function checkNudgedTooManyTimes(user,nudgeType,tooManyTimes=NUDGE_TOO_MANY_TIMES_DEFAULT) {
        if(user.settings && user.settings.nudge) {
            if(user.settings.nudge[nudgeType]) {
                if(user.settings.nudge[nudgeType].fibonacci > tooManyTimes) {
                    return true;
                }
            }
        }
        return false;
    }

    // Get User first (from db updated)
    useEffect(() => {
        let isMounted = true;
        
        async function fetchUser() {
            const tempUser = await getUserMe();
            if(isMounted) {
                setUserSession(getToken(),tempUser);
                setUser(tempUser);
            }
        }

        fetchUser();
        
        return () => { isMounted = false; };
    },[]);

    // 2 --> Get usercaps and usermotions
    useEffect(() => {
        let isMounted = true;

        async function fetchUserMotions(userId) {
            const tempUserMotions = await getUserMotions(userId);
            if(isMounted)
                setUserMotions(tempUserMotions);
        }

        async function fetchSharedCaps(userId) {
            const tempSharedCaps = await getSharedCaps(userId);
            if(isMounted) 
                setSharedCaps(tempSharedCaps);
        }

        if(user) {
            fetchUserMotions(user.userId);
            fetchSharedCaps(user.userId);
        }
        return () => { isMounted = false; };
    },[user]);

    useEffect(() => {
        let isMounted = true;
        // NUDGE when user has no userMotions [including deleted ones - at least they tried] 
        if(user && showError!==null && !showError && user.settings) {  // make sure no nudge is already active
            if(userMotions!==null) { // done fetching userMotions
                if(userMotions.length===0) {
                    const mobile = mobileCheck();
                    const permissions = notificationsAreSupported() && Notification.permission;
                    let nudgeType;
                    // Enabled everything but no specific notification configs created
                    if(!mobile && user.settings.desktopEnabled && permissions==="granted") {
                        nudgeType = NUDGE_TYPE_MOTION_DESKTOP_NOTIFICATIONS;
                        if(shouldINudge(user,nudgeType)) {
                            if(isMounted) {
                                showNudge(user,motionContent,nudgeType);
                            }
                        }
                    } else if(mobile && user.settings.appEnabled && permissions==="granted") {
                        nudgeType = NUDGE_TYPE_MOTION_APP_NOTIFICATIONS;
                        if(shouldINudge(user,nudgeType)) {
                            if(isMounted) {
                                showNudge(user,motionContent,nudgeType);
                            }
                        }
                    }
                }
            }
        }
        return () => { isMounted = false; };
    },[user,userMotions]);

    // MAIN FUNCTION FOR CALLING NUDGES
    useEffect(() => {
        let isMounted = true;
        if(userMeetsRequirements) {
            const mobile = mobileCheck();
            const pwa = pwaInstalled();
            const userPWA = user && user.settings && user.settings.pwaInstalled
            const permissions = notificationsAreSupported() && Notification.permission;  

            // Don't run if already has error message
            if(showError!==null && !showError && user) { 
                let nudged = false;
                let nudgeType;
                // Haven't install pwa yet
                if(mobile && !pwa && !userPWA) {
                    nudgeType = NUDGE_TYPE_PWA;
                    if(shouldINudge(user,nudgeType)) {
                        if(checkNudgedTooManyTimes(user,nudgeType,NUDGE_TOO_MANY_TIMES_PWA)) {
                            if(isMounted) {
                                showNudge(user,pwaDismissContent,nudgeType);
                                nudged = true;
                            }
                        } else {
                            if(isMounted) {
                                showNudge(user,pwaContent,nudgeType);
                                nudged = true;
                            }
                        }
                    }
                }
                // Haven't looked at notifications yet (mobile)
                if(!nudged && mobile && (user.settings == null || user.settings.appEnabled == null)) {
                    nudgeType = NUDGE_TYPE_APP_NOTIFICATIONS;
                    if(shouldINudge(user,nudgeType)) {
                        if(checkNudgedTooManyTimes(user,nudgeType)) {
                            if(isMounted) {
                                showNudge(user,notificationsDismissContent,nudgeType);
                                nudged = true;
                            }
                        } else {
                            if(isMounted) {
                                showNudge(user,notificationsContent,nudgeType);
                                nudged = true;
                            }
                        }
                        
                    }
                }
                // Haven't looked at notificatinos yet (desktop)
                if(!nudged && !mobile && (user.settings == null || user.settings.desktopEnabled == null)) {
                    nudgeType = NUDGE_TYPE_DESKTOP_NOTIFICATIONS;
                    if(shouldINudge(user,nudgeType)) {
                        if(checkNudgedTooManyTimes(user,nudgeType)) {
                            if(isMounted) {
                                showNudge(user,notificationsDismissContent,nudgeType);
                                nudged = true;
                            }
                        } else {
                            if(isMounted) {
                                showNudge(user,notificationsContent,nudgeType);
                                nudged = true;
                            }
                        }
                    }
                }
                // Want Desktop notifications, but blocked
                if(!nudged && !mobile && user.settings && user.settings.desktopEnabled && permissions!=="granted") {
                    if(shouldINudge(user,NUDGE_TYPE_BLOCKED_DESKTOP_NOTIFICATIONS)) {
                        if(isMounted) {
                            showNudge(user,blockedContent,NUDGE_TYPE_BLOCKED_DESKTOP_NOTIFICATIONS);
                            nudged = true;
                        }
                    }
                }
                // Want app notifications, but blocked
                if(!nudged && mobile && user.settings && user.settings.appEnabled && permissions!=="granted") {
                    if(shouldINudge(user,NUDGE_TYPE_BLOCKED_APP_NOTIFICATIONS)) {
                        if(isMounted) {
                            showNudge(user,blockedContent,NUDGE_TYPE_BLOCKED_APP_NOTIFICATIONS);
                            nudged = true;
                        }
                    }
                }
                // User has enabled stuff but not setup any app notifications (power)
                if(!nudged && sharedCaps!==null) {
                    let powerAppNotficationsPerCap = false;
                    for(var i=0; i<sharedCaps.length; i++) {
                        if(sharedCaps[i].appNotifications) {
                            powerAppNotficationsPerCap = true;
                            break;
                        }
                    }
                    if(!powerAppNotficationsPerCap) {
                        if(!mobile && user.settings && user.settings.desktopEnabled && permissions==="granted") {
                            nudgeType = NUDGE_TYPE_POWER_DESKTOP_NOTIFICATIONS;
                            if(shouldINudge(user,nudgeType)) {
                                // Defaulty has dimsiss button, no need for different message after N times
                                if(isMounted) {
                                    showNudge(user,powerContent,nudgeType);
                                    nudged = true;
                                }
                            }
                        } else if(mobile && user.settings && user.settings.appEnabled && permissions==="granted") {
                            nudgeType = NUDGE_TYPE_POWER_APP_NOTIFICATIONS;
                            if(shouldINudge(user,nudgeType)) {
                                // Defaulty has dimsiss button, no need for different message after N times
                                if(isMounted) {
                                    showNudge(user,powerContent,nudgeType);
                                    nudged = true;
                                }
                            }
                        }
                    }
                }
                // TESTING TODO TODO TODO REMOVE
                // nudgeType = NUDGE_TYPE_DESKTOP_NOTIFICATIONS
                // showNudge(user,notificationsContent,nudgeType);
                // if(shouldINudge(user,nudgeType))
                //     showNudge(user,motionContent,nudgeType);
            }
        }
        return () => { isMounted = false; };
    },[userMeetsRequirements]);

    useEffect(() => {
        let isMounted = true;
        // FIRST CHECK TO SEE IF USER HAS ACCESS FOR CAPS THAT MAKE SENSE TO NUDGE THEM
        if(!userMeetsRequirements && sharedCaps) {
            let access = false;
            if(user && user.role==='ADMIN') {
                access = true;
            } else {
                for(var i=0; i<sharedCaps.length; i++) {
                    if(sharedCaps[i].accessType==='VIEWER') {
                        access = true;
                        break;
                    }
                    if(sharedCaps[i].accessType==='OWNER') {
                        access = true;
                        break;
                    }
                }
            }
            if(isMounted && access) {
                setUserMeetsRequirements(true);
            }
        }
        return () => { isMounted = false; };
    },[sharedCaps]);

    return (<div></div>);
}